// Assembly — the whole activation pack as one printable HTML document.
//
// This is the automation the page fires. It is the browser-side twin of HG-CC's
// `scripts/collateral/build-activation-pack.mjs`: same pages, same geometry,
// same QR floor. The differences are deliberate and only two:
//
//   1. The terminal builder renders through Playwright and writes a PDF. Here
//      the browser is the renderer, and "Save as PDF" in the print dialog
//      produces the same vector document with live text.
//   2. Every piece is optional. A customer who only wants stickers and a table
//      tent prints exactly that, and the contents table and page numbers follow.
//
// Page numbering is derived once, in `buildPackHtml`, and handed to every page
// builder, so the contents table and the printed page numbers cannot drift.

import { coverPage, contentsPage, dividerPage, specPage, type ContentsRow } from "./frontmatter";
import {
  cardsPage,
  clingPage,
  stickerPage,
  stripsPage,
  tentPage,
  type PackPagePart,
  type StickerOptions,
} from "./physical";
import { POSTER_CATALOGUE, posterPage, posterSpecs, type PosterSpec } from "./posters";
import { printable } from "./qr";
import { brandCss, docCss, escapeHtml } from "./tokens";
import type { PackKit } from "@/types/activation/packKit";

/** Every optional piece, keyed the way the UI toggles them. */
export interface PackSelection {
  /** Poster ids from `posterSpecs`. Empty means no poster section at all. */
  posters: string[];
  round60: boolean;
  round50: boolean;
  square75: boolean;
  label90: boolean;
  clings: boolean;
  tent: boolean;
  cards: boolean;
  strips: boolean;
  spec: boolean;
}

/** Everything printed. What a new genie opens on. */
export const ALL_SELECTED: PackSelection = {
  posters: POSTER_CATALOGUE.map((p) => p.id),
  round60: true,
  round50: true,
  square75: true,
  label90: true,
  clings: true,
  tent: true,
  cards: true,
  strips: true,
  spec: true,
};

/**
 * A saved selection, reconciled against the catalogue as it stands now.
 *
 * Poster ids are stored on the saved row, so a row written before a catalogue
 * change carries ids that no longer exist and misses ones that were added. The
 * 2026-08-06 copy revision did exactly that: it retired "three-steps" and
 * "what-to-ask" and added "introducing" and "what-it-covers".
 *
 * Unknown ids are dropped, because a phantom id silently prints nothing. And a
 * row that carried one is a row saved under an older catalogue, so it also gets
 * the current default poster back — a customer reprinting their pack should not
 * quietly lose the piece we now lead with.
 */
export const reconcileSelection = (saved: PackSelection | null | undefined): PackSelection => {
  if (!saved) return ALL_SELECTED;
  const known = new Set(POSTER_CATALOGUE.map((p) => p.id));
  const kept = saved.posters.filter((id) => known.has(id));
  const stale = kept.length !== saved.posters.length;
  const posters = stale && !kept.includes(DEFAULT_POSTER_ID) ? [DEFAULT_POSTER_ID, ...kept] : kept;
  return { ...saved, posters };
};

/** The poster we lead with. Always printed unless a rep deliberately drops it. */
export const DEFAULT_POSTER_ID = "introducing";

/** The short run: one poster, one sticker sheet, cards. Fits an office printer. */
export const ESSENTIALS_SELECTED: PackSelection = {
  posters: ["introducing", "big-qr"],
  round60: true,
  round50: false,
  square75: false,
  label90: false,
  clings: false,
  tent: true,
  cards: true,
  strips: false,
  spec: true,
};

export interface BuiltPack {
  /** A complete standalone document. Rendered in the preview iframe as-is. */
  html: string;
  pages: number;
  /** One reading per distinct code size, for the scannability panel. */
  codes: { label: string; sizeMm: number; moduleMm: number; ecc: string; modules: number }[];
  /** The finished sheet list, for the on-screen summary. */
  contents: ContentsRow[];
  /**
   * Pieces left out because this genie's URL cannot be printed on them at a
   * scannable size. Empty for almost every genie.
   */
  dropped: string[];
}

/** The pieces that live under each divider, so an empty section drops its divider. */
const hasStickerSheet = (s: PackSelection) => s.round60 || s.round50 || s.square75 || s.label90;
const hasCounterPiece = (s: PackSelection) => s.tent || s.cards || s.strips;

/**
 * The four sticker sheets the pack carries, in print order. Held as data rather
 * than inline so the viability pass and the render pass cannot drift.
 */
const PACK_STICKERS: { flag: "round60" | "round50" | "square75" | "label90"; opts: StickerOptions }[] = [
  {
    flag: "round60",
    opts: {
      shape: "round", sizeMm: 60, cols: 3, rows: 4, label: "stickers-round-60mm",
      note: "Round stickers, 60mm, 12 up. Cut on the dashed circle. Vinyl with a matte laminate for outdoor and vehicle use.",
    },
  },
  {
    flag: "round50",
    opts: {
      shape: "round", sizeMm: 50, cols: 3, rows: 5, label: "stickers-round-50mm", variant: "qr",
      note: "Round stickers, 50mm, 15 up. Code led, for small surfaces: drink bottles, tool cases, phone cases, keyrings.",
    },
  },
  {
    flag: "square75",
    opts: {
      shape: "square", sizeMm: 75, cols: 2, rows: 3, label: "stickers-square-75mm",
      note: "Square stickers, 75mm with rounded corners, 6 up. Cut on the dashed guide. Doors, bins, equipment cases, noticeboards.",
    },
  },
  {
    flag: "label90",
    opts: {
      shape: "rect", wMm: 90, hMm: 35, cols: 2, rows: 7, label: "stickers-label-90x35mm",
      note: "Label stickers, 90 x 35mm, 14 up. Cut on the dashed guide. Shelf edges, folders, machine panels, anywhere a circle will not sit.",
    },
  },
];

/** Plain-English name for a piece the URL is too long to print on. */
const DROPPED_LABEL: Record<string, string> = {
  "stickers-round-60mm": "60mm round stickers",
  "stickers-round-50mm": "50mm round stickers",
  "stickers-square-75mm": "75mm square stickers",
  "stickers-label-90x35mm": "90 x 35mm labels",
  strips: "counter strips",
  cards: "pocket cards",
  "table-tent": "the table tent",
};

export async function buildPackHtml(kit: PackKit, selection: PackSelection): Promise<BuiltPack> {
  if (!kit.url) throw new Error("Pick a genie first. Every code in the pack points at its live page.");

  // ─── Viability, before numbering ───────────────────────────────────────────
  // A long genie slug pushes the smallest pieces under the printed-module floor.
  // Failing the whole pack over one sheet would leave that customer with nothing;
  // dropping the sheet and saying so on the contents page leaves them with
  // everything that does work.
  const dropped: string[] = [];
  const ok: Record<string, boolean> = {};
  for (const { flag, opts } of PACK_STICKERS) {
    if (!selection[flag]) continue;
    ok[flag] = (await printable(() => stickerPage(kit, opts, 0))) !== null;
    if (!ok[flag]) dropped.push(DROPPED_LABEL[opts.label] ?? opts.label);
  }
  if (selection.strips) {
    ok.strips = (await printable(() => stripsPage(kit, 0))) !== null;
    if (!ok.strips) dropped.push(DROPPED_LABEL.strips);
  }
  if (selection.cards) {
    ok.cards = (await printable(() => cardsPage(kit, 0))) !== null;
    if (!ok.cards) dropped.push(DROPPED_LABEL.cards);
  }

  const live: PackSelection = {
    ...selection,
    round60: selection.round60 && ok.round60 !== false,
    round50: selection.round50 && ok.round50 !== false,
    square75: selection.square75 && ok.square75 !== false,
    label90: selection.label90 && ok.label90 !== false,
    strips: selection.strips && ok.strips !== false,
    cards: selection.cards && ok.cards !== false,
  };
  selection = live;

  const specs = posterSpecs(kit).filter((s) => selection.posters.includes(s.id));
  const stickerSection = hasStickerSheet(selection) || selection.clings;
  const counterSection = hasCounterPiece(selection);

  // ─── Numbering, derived once ───────────────────────────────────────────────
  let n = 0;
  const P = {
    cover: ++n,
    contents: ++n,
    posterDivider: specs.length ? ++n : 0,
    posters: specs.map(() => ++n),
    stickerDivider: stickerSection ? ++n : 0,
    round60: selection.round60 ? ++n : 0,
    round50: selection.round50 ? ++n : 0,
    square75: selection.square75 ? ++n : 0,
    label90: selection.label90 ? ++n : 0,
    cling: selection.clings ? ++n : 0,
    counterDivider: counterSection ? ++n : 0,
    tent: selection.tent ? ++n : 0,
    cards: selection.cards ? ++n : 0,
    strips: selection.strips ? ++n : 0,
    spec: selection.spec ? ++n : 0,
  };
  const total = n;

  const contents: ContentsRow[] = [];
  if (specs.length) {
    const first = P.posters[0];
    const last = P.posters[P.posters.length - 1];
    contents.push({
      page: first === last ? String(first) : `${first}-${last}`,
      what: specs.length === 1 ? "Poster" : `Posters, ${specs.length} designs`,
      detail: "wall, door, noticeboard",
      size: "A4 210 x 297mm",
      stock: "170gsm satin, or plain A4",
      spec: true,
    });
  }
  if (P.round60)
    contents.push({ page: String(P.round60), what: "Round stickers, 60mm", detail: "12 up, gates and gear", size: "60mm circle, 12 up", stock: "White vinyl, matte laminate", spec: true });
  if (P.round50)
    contents.push({ page: String(P.round50), what: "Round stickers, 50mm", detail: "15 up, code only", size: "50mm circle, 15 up", stock: "White vinyl, matte laminate", spec: true });
  if (P.square75)
    contents.push({ page: String(P.square75), what: "Square stickers, 75mm", detail: "6 up, doors and cases", size: "75mm square, 6 up", stock: "White vinyl, matte laminate", spec: true });
  if (P.label90)
    contents.push({ page: String(P.label90), what: "Label stickers, 90 x 35mm", detail: "14 up, shelf edges", size: "90 x 35mm, 14 up", stock: "White vinyl or paper label", spec: true });
  if (P.cling)
    contents.push({ page: String(P.cling), what: "Window clings", detail: "150mm and 90mm circles", size: "150mm and 90mm circles", stock: "Static cling film or white vinyl", spec: true });
  if (P.tent)
    contents.push({ page: String(P.tent), what: "Table tent", detail: "folds once, both faces upright", size: "A4, folds to 210 x 148.5mm", stock: "300gsm silk", spec: true });
  if (P.cards)
    contents.push({ page: String(P.cards), what: "Pocket cards", detail: "8 up, hand them out", size: "90 x 55mm, 8 up", stock: "350gsm matte", spec: true });
  if (P.strips)
    contents.push({ page: String(P.strips), what: "Counter strips", detail: "6 up, counter or door edge", size: "186 x 38mm, 6 up", stock: "300gsm or vinyl", spec: true });
  if (P.spec)
    contents.push({ page: String(P.spec), what: "Print spec", detail: "hand this page to your printer", size: "A4", stock: "Reference only", spec: false });

  // ─── Pages ─────────────────────────────────────────────────────────────────
  const parts: PackPagePart[] = [];
  parts.push(
    await coverPage(kit, {
      eyebrow: "Activation pack",
      headline: "Your genie.<br>Out in<br>the world.",
      lede: `${total} pages of print ready material. Posters for the wall, stickers for the gate, clings for the glass, a table tent for the counter and cards for the pocket. Every code points at your live genie. Print at 100 percent on A4 and start putting them up.`,
    }),
  );
  parts.push(
    contentsPage(
      {
        title: "What is in this pack",
        lede: "Print the whole thing, or just the pages you need. Every page is A4 portrait, so nothing needs a tray change. Print at 100 percent with scaling off, or the codes come out smaller than they are meant to be.",
        rows: contents,
        panelTitle: "Where to put it",
        panelItems: kit.placements ?? [],
        notes: [
          ["Print settings", 'A4, portrait, 100 percent scale, "actual size", borderless off. Colour on.'],
          ["Before you stick anything", "Print one page, scan the code with your phone, confirm it opens the genie."],
          dropped.length
            ? [
                "Not in this pack",
                `${dropped.join(", ")}. Your genie's web address is ${kit.url.length} characters, and at that size the code would print too small to scan reliably.`,
              ]
            : ["Outdoors", "Stickers and clings need vinyl with a matte laminate. Plain paper will not last a winter."],
        ],
      },
      P.contents,
    ),
  );

  if (P.posterDivider) {
    parts.push(
      dividerPage(
        kit,
        "Posters.",
        specs.length > 1
          ? "Designs for the wall, the door, the noticeboard and the staff room. Print the ones that suit the room."
          : "One design for the wall, the door or the noticeboard.",
        P.posterDivider,
        "dark",
      ),
    );
  }
  for (let i = 0; i < specs.length; i++) {
    parts.push(await posterPage(kit, specs[i], P.posters[i]));
  }

  if (P.stickerDivider) {
    parts.push(
      dividerPage(
        kit,
        "Stickers and clings.",
        "Cut on the dashed guide. If it is going outside or on a vehicle, print on vinyl with a matte laminate.",
        P.stickerDivider,
        "primary",
      ),
    );
  }
  for (const { flag, opts } of PACK_STICKERS) {
    const page = P[flag];
    if (page) parts.push(await stickerPage(kit, opts, page));
  }
  if (P.cling) parts.push(await clingPage(kit, P.cling));

  if (P.counterDivider) {
    parts.push(
      dividerPage(
        kit,
        "Counter and pocket.",
        "A table tent for the counter, a stack of cards to hand out, and strips for a shelf edge or a door plate. This is the material that travels.",
        P.counterDivider,
        "secondary",
      ),
    );
  }
  if (P.tent) parts.push(await tentPage(kit, P.tent));
  if (P.cards) parts.push(await cardsPage(kit, P.cards));
  if (P.strips) parts.push(await stripsPage(kit, P.strips));
  if (P.spec) {
    parts.push(
      specPage(
        kit,
        {
          title: "Print spec",
          lede: "Hand this page to a print shop with the PDF and they can quote the job without a follow up email. If you are printing it yourself, the office printer settings are on page 2.",
          rows: contents,
          extraNotes: [
            "Sheets with a dashed outline are multi up. Kiss cut the stickers and clings, guillotine the cards and strips.",
          ],
        },
        P.spec,
      ),
    );
  }

  const html = `<!doctype html><html><head><meta charset="utf-8">
    <title>${escapeHtml(kit.name)} Activation Pack</title>
    <style>
      ${docCss(kit.brand)}
      ${brandCss()}
      ${parts.map((p) => p.css).join("\n")}
    </style></head><body>
    ${parts.map((p) => p.html).join("\n")}
  </body></html>`;

  // One reading per distinct printed size. Codes within a page are identical,
  // so the first on each page covers every size in the document.
  const seen = new Set<number>();
  const codes: BuiltPack["codes"] = [];
  for (const part of parts) {
    if (!part.qr) continue;
    const key = Math.round(part.qr.sizeMm * 10);
    if (seen.has(key)) continue;
    seen.add(key);
    codes.push({
      label: part.qr.label,
      sizeMm: part.qr.sizeMm,
      moduleMm: part.qr.qr.moduleMm,
      ecc: part.qr.qr.ecc,
      modules: part.qr.qr.modules,
    });
  }
  codes.sort((a, b) => a.sizeMm - b.sizeMm);

  return { html, pages: total, codes, contents, dropped };
}

export type { PosterSpec };
